import React from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Loader2, Mail, Sparkles } from "lucide-react";

const UserEmailMergeDialog = React.memo(function UserEmailMergeDialog({
  open, onOpenChange, selectedUsers, emailSubject, setEmailSubject,
  emailBody, setEmailBody, fromEmail, setFromEmail, connectedEmails,
  sendingEmails, rewritingWithAi, onSend, onRewriteWithAi, insertVariable,
  currentUser, selectedUserIds
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-600" />
            Send Email to Selected Users
          </DialogTitle>
          <DialogDescription>
            Compose an email to send to {selectedUserIds.size} selected user(s).
            Use merge variables to personalize each email.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto space-y-4 py-4">
          {/* From Email Info */}
          <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-700">From:</span>
              <span className="text-sm text-slate-600">
                {currentUser?.full_name || currentUser?.email || 'You'}
              </span>
              <span className="text-xs text-slate-400">
                ({currentUser?.email})
              </span>
            </div>
          </div>

          {/* Merge Variables */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <Label className="text-sm font-medium text-blue-800 mb-2 block">
              Available Merge Variables (click to insert):
            </Label>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'full_name', label: 'Full Name' },
                { key: 'first_name', label: 'First Name' },
                { key: 'email', label: 'Email' },
                { key: 'role', label: 'Role' },
                { key: 'team_names', label: 'Team Names' }
              ].map(({ key, label }) => (
                <Button
                  key={key}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="bg-white text-blue-700 border-blue-300 hover:bg-blue-100"
                  onClick={() => insertVariable(key)}
                >
                  {`{{${key}}}`}
                  <span className="ml-1 text-xs text-slate-500">({label})</span>
                </Button>
              ))}
            </div>
          </div>

          {/* From Email */}
          <div className="space-y-2">
            <Label>From</Label>
            <Select value={fromEmail} onValueChange={setFromEmail}>
              <SelectTrigger>
                <SelectValue placeholder="Select sender email" />
              </SelectTrigger>
              <SelectContent>
                {connectedEmails.map(email => (
                  <SelectItem key={email} value={email}>{email} (Office 365)</SelectItem>
                ))}
                <SelectItem value="no-reply@jobtalk.ai">no-reply@jobtalk.ai (SendGrid)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Subject */}
          <div className="space-y-2">
            <Label htmlFor="email-subject">Subject</Label>
            <Input
              id="email-subject"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              placeholder="Enter email subject (can include merge variables)"
              data-testid="email-subject-input"
            />
          </div>

          {/* Body */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="email-body">Message Body</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onRewriteWithAi}
                disabled={rewritingWithAi || !emailBody.trim()}
                className="text-purple-600 border-purple-300 hover:bg-purple-50"
              >
                {rewritingWithAi ? (
                  <>
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                    Rewriting...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-1 h-3 w-3" />
                    Fix with AI
                  </>
                )}
              </Button>
            </div>
            <textarea
              id="email-body"
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              placeholder={"Enter your message here...\n\nUse merge variables like {{full_name}} to personalize each email."}
              className="w-full min-h-[200px] px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-y"
              data-testid="email-body-input"
            />
          </div>

          {/* Preview */}
          {selectedUsers.length > 0 && emailBody && (
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Preview (first recipient: {selectedUsers[0]?.full_name}):
              </Label>
              <div className="text-sm text-slate-600 whitespace-pre-wrap">
                {emailBody
                  .replace(/\{\{full_name\}\}/g, selectedUsers[0]?.full_name || '')
                  .replace(/\{\{first_name\}\}/g, (selectedUsers[0]?.full_name || '').split(' ')[0])
                  .replace(/\{\{email\}\}/g, selectedUsers[0]?.email || '')
                  .replace(/\{\{role\}\}/g, selectedUsers[0]?.role || '')
                  .replace(/\{\{team_names\}\}/g, (selectedUsers[0]?.team_names || []).join(', ') || 'No team')
                }
              </div>
            </div>
          )}

          {/* Recipients List */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Recipients ({selectedUserIds.size}):</Label>
            <div className="max-h-32 overflow-auto p-2 bg-slate-50 rounded-md">
              <div className="flex flex-wrap gap-1">
                {selectedUsers.map(user => (
                  <Badge key={user.id} variant="outline" className="text-xs">
                    {user.full_name}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onSend}
            disabled={sendingEmails || !emailSubject.trim() || !emailBody.trim()}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="send-bulk-email-btn"
          >
            {sendingEmails ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="mr-2 h-4 w-4" />
                Send to {selectedUserIds.size} User(s)
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

export default UserEmailMergeDialog;
